/**
 * Which camera this terminal uses, remembered across reloads.
 *
 * A desk often has two: the laptop's own and a document camera pointed at the counter. Picking the
 * right one every time somebody reloads the page is the kind of friction that ends with photographs
 * taken on the wrong camera, so the choice belongs to the TERMINAL rather than to the operator —
 * which is also why it is in `localStorage` and not in the session: the next person on shift wants
 * the same camera, not the same person's preference.
 *
 * **A device id is not personal data**, and nothing else is written: it is an opaque handle the
 * browser mints for a piece of hardware, scoped to this origin.
 *
 * **A remembered device that is no longer attached is ignored rather than obeyed.** Hardware gets
 * unplugged; asking for a `deviceId` that does not exist makes `getUserMedia` refuse with
 * `OverconstrainedError`, which reads to an operator as "the camera is broken".
 */

/** Where the choice lives. Namespaced like every other key this application writes. */
export const CAMERA_STORAGE_KEY = 'sentra.cameraDeviceId';

/** The slice of `Storage` this module uses, so a test hands in a double rather than a whole DOM. */
export type DeviceStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** What this terminal remembers, or `null`. A storage that throws remembers nothing. */
export function rememberedCamera(storage: DeviceStorage): string | null {
  try {
    const held = storage.getItem(CAMERA_STORAGE_KEY);
    return held === null || held.length === 0 ? null : held;
  } catch {
    return null;
  }
}

export function rememberCamera(storage: DeviceStorage, deviceId: string): void {
  try {
    storage.setItem(CAMERA_STORAGE_KEY, deviceId);
  } catch {
    // A terminal that cannot remember still takes photographs. See the module note.
  }
}

/**
 * The camera to open: the remembered one when it is still attached, otherwise the first.
 *
 * Returning `null` for an empty list rather than a made-up id is what lets the caller say "this
 * machine has no camera" instead of asking for one and reporting whatever the platform threw.
 */
export function chooseCamera(
  available: readonly { readonly deviceId: string }[],
  remembered: string | null,
): string | null {
  if (available.length === 0) {
    return null;
  }
  const stillAttached = available.some((device) => device.deviceId === remembered);
  return stillAttached && remembered !== null ? remembered : (available[0]?.deviceId ?? null);
}
